import { Badge, Box, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import useMount from 'hooks/useMount';
import useNotifications from 'store/notifications';
import { IPlayerNotification, PlayerNotificationType } from 'types/notifications';
import useScreen from 'hooks/useScreen';

import { calculateBackTime } from './utils';
import useTranslates from 'utils/translate';
import useSelectedLanguage from 'store/language';

interface NotificationDrawerProps {
  onClose: () => void;
  anchorEl: null | HTMLElement;
}

const TABS = {
  ALL: 'all',
  UNREAD: 'unread',
};

const NotificationDrawer: FC<NotificationDrawerProps> = ({ onClose, anchorEl }) => {
  const { translate } = useTranslates();
  const [{ selectedLanguage }] = useSelectedLanguage();
  const [notificationsState, notificationsActions] = useNotifications();
  const [activeTab, setActiveTab] = useState(TABS.UNREAD);
  const navigate = useNavigate();
  const { isMobile } = useScreen();

  useMount(() => {
    notificationsActions.get();
  });

  const handleNotification = (notification: IPlayerNotification): void => {
    notificationsActions.updateNotificationById(notification.id);
    onClose();
    switch (notification.template.type) {
      case PlayerNotificationType.ACCOUNT_VERIFIED: {
        break;
      }
      case PlayerNotificationType.AVAILABLE_BONUS: {
        navigate('/bonuses');
        break;
      }
      case PlayerNotificationType.AVAILABLE_BONUS_EXPIRED: {
        navigate('/bonus-history');
        break;
      }
      case PlayerNotificationType.WITHDRAW_APPROVED: {
        navigate('/wallet-history');
        break;
      }
      case PlayerNotificationType.DEPOSIT_SETTLED: {
        navigate('/wallet-history');
        break;
      }
      case PlayerNotificationType.WITHDRAW_REJECTED: {
        navigate('/wallet-history');
        break;
      }
      default:
        break;
    }
  };

  const filteredNotifications = useMemo(() => {
    if (activeTab === TABS.UNREAD) {
      return notificationsState.data.filter((notification) => !notification.seenAt);
    }

    return notificationsState.data;
  }, [activeTab, notificationsState.data]);

  const unreadNotificationsExists = filteredNotifications.length > 0;

  return (
    <Menu
      id="notification-menu"
      anchorEl={anchorEl}
      sx={isMobile ? { '.MuiMenu-paper': { top: '0px !important', left: '0px !important' } } : {}}
      slotProps={{
        paper: {
          style: {
            height: isMobile ? '100%' : 500,
            width: isMobile ? '100%' : 400,
            ...(isMobile
              ? {
                  borderRadius: 0,
                  maxWidth: '100%',
                  maxHeight: 'calc(100% - 60px)',
                }
              : {}),
          },
        },
        root: {
          style: {
            width: isMobile ? '100%' : 400,
          },
        },
      }}
      open
      onClose={onClose}
    >
      <Tabs
        value={activeTab}
        sx={{
          '.MuiTabs-flexContainer': {
            justifyContent: isMobile ? 'center' : 'flex-start',
          },
          '.MuiTab-root': {
            flex: isMobile ? 1 : 'unset',
          },
        }}
        style={{ padding: isMobile ? 0 : '0px 26px' }}
        onChange={(_, value): void => {
          setActiveTab(value);
        }}
      >
        <Tab label={translate('unread')} value={TABS.UNREAD}></Tab>
        <Tab label={translate('all')} value={TABS.ALL}></Tab>
      </Tabs>
      <Box display="flex" flexDirection="column" alignItems="center">
        {!unreadNotificationsExists && <MenuItem>No notifications</MenuItem>}
        {unreadNotificationsExists && (
          <>
            {filteredNotifications.map((notification) => {
              const currentLanguageTemplate =
                notification.template.translations.find(
                  (translation) => translation.languageId === selectedLanguage.langId
                ) || notification.template.translations[0];

              if (!currentLanguageTemplate) return null;

              return (
                <MenuItem
                  style={{
                    gap: '8px',
                    width: '100%',
                    justifyContent: 'space-between',
                    opacity: notification.seenAt ? 0.6 : 1,
                  }}
                  key={notification.id}
                  onClick={(): void => handleNotification(notification)}
                >
                  <Box display="flex" alignItems="center" gap="8px">
                    <Box display="flex">
                      <AttachMoneyIcon color="primary" />
                    </Box>
                    <Box>
                      <Typography fontWeight="500">{currentLanguageTemplate.title}</Typography>
                      <Typography color="GrayText" style={{ whiteSpace: 'normal' }}>
                        {currentLanguageTemplate.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    alignSelf="center"
                    flexDirection="column"
                    gap="8px"
                    justifyContent="space-between"
                  >
                    {!notification.seenAt && (
                      <Badge color="primary" variant="dot" style={{ alignSelf: 'end', marginRight: '4px' }} />
                    )}
                    <Typography display="flex" fontSize="12px" color="GrayText">
                      {calculateBackTime(notification.createdAt)}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </>
        )}
      </Box>
    </Menu>
  );
};

export default NotificationDrawer;
